import * as Yup from 'yup';
import { useMemo, useEffect, useState, useCallback } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
// @mui
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
// hooks
import {
  Autocomplete,
  Button,
  Chip,
  IconButton,
  TextField,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useNavigate, useParams } from 'react-router';
// routes
import { paths } from 'src/routes/paths';
// components
import Iconify from 'src/components/iconify';
import { useSnackbar } from 'src/components/snackbar';
import FormProvider, {
  RHFSelect,
  RHFTextField,
  RHFUpload,
} from 'src/components/hook-form';
import { uploadResumeToS3 } from 'src/services/aws.service';
import { LoadingScreen } from 'src/components/loading-screen';
import { useAuthContext } from 'src/auth/hooks';
import { getOrganizationId } from 'src/services/user.service';
import {
  CreateCandidate,
  editCandidateDetails,
  getCandidateDetails,
} from 'src/services/candidate.service';
import { useRouter, useSearchParams } from 'src/routes/hooks';
import { SourceInput } from 'src/components/source-input';
import { getTags } from 'src/services/role.service';
// ----------------------------------------------------------------------

export default function EditCandidateView() {
  // router
  const navigate = useNavigate();
  const params = useParams();
  const router = useRouter();
  const searchParams = useSearchParams();
  const returnTo = searchParams.get('returnTo');

  // hooks
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuthContext();
  const organization_id = getOrganizationId(user);

  // states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tagInputValue, setTagInputValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [inputSourceFormatOptions, setInputSourceFormatOptions] = useState([]);
  const [isFetchingTags, setIsFetchingTags] = useState(false);
  const [tags, setTags] = useState([]);

  // memo
  const defaultValues = useMemo(
    () => ({
      fullname: '',
      email: '',
      phonenumber: '',
      source: '',
      tag: [],
      coverUrl: null,
      // status: STATUS_OPTIONS[0].value
    }),
    [],
  );

  const CandidateSchema = Yup.object().shape({
    fullname: Yup.string().required('Full Name is required'),
    email: Yup.string()
      .email('Please enter a valid email')
      .required('Email is required'),
    phonenumber: Yup.string(),
    source: Yup.string(),
    // .required("Source is required"),
    tag: Yup.array(),
    // .min(1, 'Enter at least one tag'),
    coverUrl: Yup.mixed().nullable(),
    // status: Yup.string()
  });

  const CandidateMethods = useForm({
    resolver: yupResolver(CandidateSchema),
    defaultValues,
  });

  const [methods, setMethods] = useState(CandidateMethods);

  // variables
  const { setValue, handleSubmit } = methods;

  const onSubmit = handleSubmit(async (data, event) => {
    console.log('data ', data);
    try {
      setIsSubmitting(true);
      // // upload resume
      const resume_url = await uploadResumeToS3(data.coverUrl);
      // console.log(resume_url);

      // // call api
      const reqBody = {};
      if (data.fullname) reqBody.name = data.fullname;
      if (data.email) reqBody.email = data.email;
      if (data.source) reqBody.candidate_source = data.source;
      if (data.tag) reqBody.tags = data.tag;
      if (data.coverUrl) reqBody.resume_url = resume_url;
      if (data.phonenumber) reqBody.mobile_no = data.phonenumber;

      const res = await editCandidateDetails(
        organization_id,
        params.candidateId,
        reqBody,
      );
      console.log(res);
      if (res.success) {
        navigate(paths.dashboard.candidates.root);
        enqueueSnackbar('Candidate edited successfully');
      } else {
        enqueueSnackbar({ message: res.message, variant: 'error' });
      }
      setIsSubmitting(false);
    } catch (error) {
      console.error(error);
    }
  });

  const handleSingleDrop = useCallback(
    acceptedFiles => {
      const file = acceptedFiles[0];

      const newFile = Object.assign(file, {
        preview: URL.createObjectURL(file),
      });

      if (file) {
        setValue('coverUrl', newFile, { shouldValidate: true });
      }
    },
    [setValue],
  );

  const handleRemoveFile = inputFile => {
    setValue('coverUrl', null);
  };

  const fetchTags = useCallback(
    async (showLoader = true) => {
      try {
        if (showLoader) {
          setIsFetchingTags(true);
        }

        const response = await getTags(organization_id);
        if (response.success) {
          setTags(response.body);
        } else {
          throw new Error(response.message);
        }
      } catch (err) {
        console.log('err', err);
      } finally {
        setIsFetchingTags(false);
      }
    },
    [organization_id],
  );

  const renderHeading = (
    <Typography variant="h3" sx={{ mb: '24px' }}>
      Edit Candidate
    </Typography>
  );

  const renderProperties = (
    <Stack spacing="20px">
      <Stack>
        <RHFUpload
          name="coverUrl"
          maxSize={5000000}
          onRemove={handleRemoveFile}
          onDrop={handleSingleDrop}
          accept={{
            'application/pdf': ['.pdf'],
            'application/msword': ['.doc'],
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
              ['docx'],
          }}
        />
      </Stack>

      <Stack spacing="6px">
        <Typography variant="paragraph" fontWeight={600}>
          Full Name
        </Typography>
        <RHFTextField name="fullname" placeholder="Full Name" />
      </Stack>

      <Stack spacing="6px">
        <Typography variant="paragraph" fontWeight={600}>
          Email
        </Typography>
        <RHFTextField name="email" type="email" placeholder="Email" />
      </Stack>

      <Stack spacing="6px">
        <Typography variant="paragraph" fontWeight={600}>
          Phone
        </Typography>
        <RHFTextField name="phonenumber" placeholder="Phone Number" />
      </Stack>

      <Stack spacing="6px">
        <Typography variant="paragraph" fontWeight={600}>
          Source
        </Typography>
        <SourceInput
          inputSourceFormatOptions={inputSourceFormatOptions}
          setInputSourceFormatOptions={setInputSourceFormatOptions}
          methods={methods}
        />
      </Stack>

      <Stack spacing="6px">
        <Typography variant="paragraph" fontWeight={600}>
          Tags
        </Typography>
        <Controller
          name="tag"
          render={({ field, fieldState: { error } }) => (
            <Autocomplete
              {...field}
              loading={isFetchingTags}
              options={tags}
              inputValue={tagInputValue}
              onKeyDown={e => {}}
              onInputChange={(event, newInputValue) => {
                if (newInputValue.endsWith(',')) {
                  let oldValues = methods.getValues();
                  oldValues = oldValues.tag;
                  let newValueArr = newInputValue.split(',').slice(0, -1);
                  newValueArr = oldValues.concat(newValueArr);
                  methods.setValue('tag', newValueArr, {
                    shouldValidate: true,
                  });
                  setTagInputValue('');
                } else {
                  setTagInputValue(newInputValue);
                }
              }}
              onChange={(event, newValue) => {
                console.log('on change called ', newValue);
                return methods.setValue('tag', newValue, {
                  shouldValidate: true,
                });
              }}
              renderInput={params => (
                <TextField
                  placeholder="Tags"
                  error={!!error}
                  {...params}
                  helperText={error ? error?.message : ''}
                  // onChange={handleSkillChange}
                />
              )}
              multiple
              freeSolo
              disableCloseOnSelect
              renderTags={(selected, getTagProps) =>
                selected.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option}
                    label={option}
                    size="small"
                    color="info"
                    variant="soft"
                  />
                ))
              }
            />
          )}
        />
      </Stack>
    </Stack>
  );

  const renderActions = (
    <Stack mt="24px">
      <LoadingButton
        fullWidth
        variant="contained"
        loading={isSubmitting}
        type="submit"
      >
        Save
      </LoadingButton>
    </Stack>
  );

  // effects
  useEffect(() => {
    const caller = async () => {
      const res = await getCandidateDetails(
        organization_id,
        params.candidateId,
      );
      console.log(res);
      if (res.success) {
        if (res.body.name) setValue('fullname', res.body.name);
        if (res.body.email) setValue('email', res.body.email);
        if (res.body.phone_number)
          setValue('phonenumber', res.body.phone_number);
        if (res.body.candidate_source)
          setValue('source', res.body.candidate_source);
        if (res.body.tags) setValue('tag', res.body.tags);
        setLoading(false);
      }
    };
    caller();
  }, []);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  return (
    <>
      {loading && (
        <Stack
          sx={{ height: '100%', display: 'flex', justifyContent: 'center' }}
        >
          <LoadingScreen loadingText="" />
        </Stack>
      )}
      {!loading && (
        <Stack width="60%" mx="auto" my="40px">
          <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            {renderHeading}
            <IconButton
              sx={{ position: 'absolute', top: 12, right: 16 }}
              onClick={() =>
                router.push(returnTo || paths.dashboard.candidates.root)
              }
            >
              <Iconify width={30} icon="tabler:x" />
            </IconButton>
            {renderProperties}
            {renderActions}
          </FormProvider>
        </Stack>
      )}
    </>
  );
}
